import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight, Mail, Phone, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/constants";
import { HUBSPOT_MEETING_BASE } from "@/lib/meetingLinks";
import axios from "axios";

const QuoteSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      return;
    }

    const checkPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus("timeout");
        return;
      }

      try {
        const response = await axios.get(`${API}/quotes/pay/${token}/status`);
        
        if (response.data.status === "paid") {
          setStatus("success");
          setQuote(response.data.quote);
          return;
        }

        // Continue polling
        setTimeout(() => checkPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error("Error checking payment:", error);
        if (attempts < maxAttempts - 1) {
          setTimeout(() => checkPaymentStatus(attempts + 1), pollInterval);
        } else {
          setStatus("error");
        }
      }
    };

    checkPaymentStatus();
  }, [token]);

  const formatCurrency = (amount, currency = "usd") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase()
    }).format(amount);
  };

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="quote-success-page">
      <div className="container-custom">
        <div className="max-w-2xl mx-auto text-center">
          {status === "loading" && (
            <div className="space-y-6">
              <div className="w-20 h-20 rounded-full bg-[#00E676]/20 flex items-center justify-center mx-auto">
                <Loader2 className="w-10 h-10 text-[#00E676] animate-spin" />
              </div>
              <h1 className="text-3xl font-bold">Processing Your Payment...</h1>
              <p className="text-gray-400">Please wait while we confirm your payment.</p>
            </div>
          )}

          {status === "success" && (
            <div className="space-y-6">
              <div className="w-20 h-20 rounded-full bg-[#00E676]/20 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-10 h-10 text-[#00E676]" />
              </div>
              <h1 className="text-4xl font-bold">Payment Successful!</h1>
              <p className="text-xl text-gray-400">
                Thank you for your payment. We're excited to work with you!
              </p>
              
              {quote && (
                <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6 mt-8 text-left">
                  <h3 className="text-lg font-semibold mb-4">Payment Details</h3>
                  <div className="space-y-3 text-gray-400">
                    <div className="flex justify-between">
                      <span>Quote #</span>
                      <span className="text-white">{quote.id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Service</span>
                      <span className="text-white">{quote.service_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Amount Paid</span>
                      <span className="text-[#00E676] font-semibold">
                        {formatCurrency(quote.amount, quote.currency)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Status</span>
                      <span className="text-[#00E676]">Confirmed</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6 mt-6">
                <h3 className="text-lg font-semibold mb-4">What Happens Next?</h3>
                <div className="space-y-4 text-left">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#00E676]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[#00E676] font-semibold text-sm">1</span>
                    </div>
                    <div>
                      <p className="text-white font-medium">Confirmation Email</p>
                      <p className="text-gray-400 text-sm">You'll receive a receipt and project details within minutes.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#00E676]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[#00E676] font-semibold text-sm">2</span>
                    </div>
                    <div>
                      <p className="text-white font-medium">Kickoff Call</p>
                      <p className="text-gray-400 text-sm">Book your onboarding call now — or we'll reach out within 24 hours.</p>
                      <a href={HUBSPOT_MEETING_BASE} target="_blank" rel="noopener noreferrer" data-testid="quote-success-book-call-btn">
                        <Button size="sm" className="btn-ninja mt-2">
                          <Calendar className="w-4 h-4 mr-2" /> Book Your Onboarding Call
                        </Button>
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#00E676]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[#00E676] font-semibold text-sm">3</span>
                    </div>
                    <div>
                      <p className="text-white font-medium">Project Begins</p>
                      <p className="text-gray-400 text-sm">We'll start working on your project immediately after kickoff.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-6 flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/">
                  <Button className="btn-ninja">
                    Return Home <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="btn-outline">Contact Us</Button>
                </Link>
              </div>
            </div>
          )}

          {(status === "error" || status === "timeout") && (
            <div className="space-y-6">
              <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto">
                <XCircle className="w-10 h-10 text-yellow-500" />
              </div>
              <h1 className="text-3xl font-bold">
                {status === "timeout" ? "Verification Timeout" : "Something Went Wrong"}
              </h1>
              <p className="text-gray-400">
                We couldn't verify your payment automatically. Don't worry - if you were charged, your payment was successful.
              </p>
              
              <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6 mt-6">
                <p className="text-white font-medium mb-4">Please contact us to confirm your payment:</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a href="mailto:info@digitalmedianinja.com" className="flex items-center gap-2 text-[#00E676] hover:underline">
                    <Mail className="w-4 h-4" />
                    info@digitalmedianinja.com
                  </a>
                  <a href="tel:+14329349470" className="flex items-center gap-2 text-[#00E676] hover:underline">
                    <Phone className="w-4 h-4" />
                    (432) 934-9470
                  </a>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <Link to="/">
                  <Button className="btn-ninja">Return Home</Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="btn-outline">Contact Support</Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteSuccessPage;
